import React, { forwardRef } from 'react';
import cn from 'classnames';
import { SIZES, LABEL_TYPES } from '../classnames';
import CLASSNAMES from './classnames';
import parseClassnames from '../../helpers/parseClassnames';
import transition from '../../styles/transition';
import T from '../../Text';
import { getDefaultIconValues, IconItemType } from '../helpers';
import { PrimaryButtonProps } from './types';

const PrimaryButton = forwardRef<any, PrimaryButtonProps>(
  (
    {
      children,
      label,
      size = 'm',
      as,
      white,
      gray,
      darkMode,
      disabled,
      full,
      leftIcon: leftIconProp,
      rightIcon: rightIconProp,
      href,
      className = '',
      style,
      labelClassName,
      ...other
    },
    ref,
  ) => {
    const leftIcon: IconItemType = getDefaultIconValues(leftIconProp);
    const rightIcon: IconItemType = getDefaultIconValues(rightIconProp);
    let Tag: keyof JSX.IntrinsicElements = 'button';

    if (as) {
      Tag = as;
    } else if (href) {
      Tag = 'a';
    }

    let buttonTypeClassNames = disabled
      ? CLASSNAMES[darkMode ? 'darkMode-disabled' : 'default-disabled']
      : CLASSNAMES[darkMode ? 'darkMode' : 'default'];

    if (gray) {
      buttonTypeClassNames = disabled
        ? CLASSNAMES[darkMode ? 'darkMode-disabled' : 'default-disabled']
        : CLASSNAMES.gray;
    }
    if (white) {
      buttonTypeClassNames = disabled
        ? CLASSNAMES['white-disabled']
        : CLASSNAMES[darkMode ? 'white-dark' : 'white'];
    }

    return (
      <Tag
        className={parseClassnames(
          className,
          cn(
            {
              'w-full': full,
            },
            SIZES[size],
            buttonTypeClassNames,
          ),
        )}
        style={{
          transition: transition(['background-color', 'color']),
          ...style,
        }}
        disabled={disabled}
        href={href}
        ref={ref}
        {...other}
      >
        {leftIcon.component && (
          <leftIcon.component
            size={leftIcon.size}
            className={cn(
              {
                'fill-current': leftIcon.hasFill,
                'stroke-current': leftIcon.hasStroke,
                'opacity-36': disabled && !white && !darkMode,
              },
              'flex-shrink-0',
            )}
            ignoreColor
          />
        )}
        {label ? (
          <T
            type={LABEL_TYPES[size]}
            color="inherit"
            className={cn(
              {
                'ml-8': !!leftIcon.component,
                'mr-8': !!rightIcon.component,
                'opacity-36': disabled && !white && !darkMode,
              },
              labelClassName,
            )}
          >
            {label}
          </T>
        ) : (
          children
        )}
        {rightIcon.component && (
          <rightIcon.component
            size={rightIcon.size}
            className={cn(
              {
                'fill-current': rightIcon.hasFill,
                'stroke-current': rightIcon.hasStroke,
                'opacity-36': disabled && !white && !darkMode,
              },
              'flex-shrink-0',
            )}
            ignoreColor
          />
        )}
      </Tag>
    );
  },
);

export default PrimaryButton;
